import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { EmailVerification } from './components/auth/EmailVerification';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { QuoteForm } from './components/QuoteForm';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { AdminTabs } from './components/admin/AdminTabs';
import { InsurerDashboard } from './components/insurerdashboard';
import { InsuranceRequestsList } from './components/InsuranceRequestsList';
import { TestBid } from './components/TestBid';
import { InsurerProfile } from './components/insurer/InsurerProfile';
import { InsurerRegistrationForm } from './components/insurer/InsurerRegistrationForm';
import { AuthProvider } from './lib/AuthContext';
import { CookieConsent } from './components/CookieConsent';
import { HowItWorks } from './pages/HowItWorks';
import { Footer } from './components/Footer';
import { useAuth } from './lib/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';

function HomePage() {
  const { user } = useAuth();
  const [showAdmin, setShowAdmin] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Check if user has admin role
  const isAdmin = user?.app_metadata?.role === 'admin';
  // Check if user has insurer role
  const isInsurer = user?.app_metadata?.role === 'insurer';
  
  // Reset states when user changes
  useEffect(() => {
    setIsLoading(false);
  }, []);

  // Reset showAdmin when user changes
  useEffect(() => {
    setShowAdmin(false);
    setShowDashboard(false);
  }, [user]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show regular user view for everyone else (including admins when not in admin mode)
  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        isAdmin={isAdmin}
        isInsurer={isInsurer}
        showAdmin={showAdmin}
        showDashboard={showDashboard}
        onToggleAdmin={() => setShowAdmin(!showAdmin)}
        onToggleDashboard={() => setShowDashboard(!showDashboard)}
      />
      <Hero />
      <div className="py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <QuoteForm onSubmitSuccess={() => setRefreshKey(prev => prev + 1)} />
            {user && <div className="mt-8"><InsuranceRequestsList key={refreshKey} /></div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function InsurerProfilePage() {
  const { user } = useAuth();
  const isInsurer = user?.app_metadata?.role === 'insurer';

  if (!isInsurer) {
    return <Navigate to="/" />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <InsurerProfile />
      </div>
    </div>
  );
}

function InsurerRegistrationPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <InsurerRegistrationForm />
      </div>
    </div>
  );
}

function TestBidPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <TestBid />
      </div>
    </div>
  );
}

// Main App component
function App() {
  const navigate = useNavigate();

  return (
    <AuthProvider>
      <ErrorBoundary>
      <Routes>
        <Route index element={<HomePage />} />
        <Route path="auth/callback" element={<EmailVerification />} />
        <Route path="register-insurer" element={<InsurerRegistrationPage />} />
        <Route path="how-it-works" element={<HowItWorks />} />
        <Route path="insurer">
          <Route path="dashboard" element={<InsurerDashboard />} />
          <Route path="profile" element={<InsurerProfilePage />} />
        </Route>
        <Route 
          path="admin/dashboard" 
          element={
            <ProtectedRoute role="admin">
              <div className="min-h-screen bg-white">
                <AdminDashboard onExit={() => navigate('/')} />
              </div>
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
      <CookieConsent />
      </ErrorBoundary>
    </AuthProvider>
  );
}

// Protected Route Component
function ProtectedRoute({ children, role }: { children: React.ReactNode; role: string }) {
  const { user } = useAuth();
  const userRole = user?.app_metadata?.role;

  if (!user || userRole !== role) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default App;